/**
 * /network/leaderboard — Full-width XP leaderboard with stats sidebar
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Medal, Star, Zap, ChevronRight, Loader2, TrendingUp, Users, Network, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { leftTopArrowGradient, rightBottomArrowGradient } from '@/assets/svgs/gradientIcons';

const RANK_STYLE = [
  { medal: '🥇', glow: 'rgba(251,191,36,0.2)',  border: 'border-amber-500/30',   bg: 'bg-amber-500/5',  text: 'text-amber-400',  xpCol: '#f5c842' },
  { medal: '🥈', glow: 'rgba(148,163,184,0.2)',  border: 'border-slate-400/30',  bg: 'bg-slate-400/5',  text: 'text-slate-300',  xpCol: '#94a3b8' },
  { medal: '🥉', glow: 'rgba(180,120,60,0.2)',   border: 'border-orange-700/30', bg: 'bg-orange-700/5', text: 'text-orange-400', xpCol: '#c97c3b' },
];

const LV_COL = ['#94a3b8','#7dd3fc','#c4b5fd','#67e8f9','#f0abfc','#62FAE3'];
const LV_LABELS = ['Newcomer','Maker','Creator','Featured','Influencer','Studio Lead'];
const lvCol   = lv => LV_COL[Math.min((lv||1)-1,5)];
const lvLabel = lv => LV_LABELS[Math.min((lv||1)-1,5)];

function RankRow({ rank, entry, onClick }) {
  const s   = RANK_STYLE[rank - 1] || {};
  const top = rank <= 3;
  const prog = entry.progress || {};
  const lv   = prog.level || 1;
  const col  = top ? s.xpCol : lvCol(lv);

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: rank * 0.035 }}
      onClick={onClick}
      className={`group cursor-pointer flex items-center gap-4 px-5 py-3.5 transition-all duration-200 border-b border-white/[0.05] last:border-0
        ${top ? `${s.border} ${s.bg}` : 'hover:bg-white/[0.04]'}`}
      style={top ? { boxShadow: `inset 0 0 30px ${s.glow}` } : undefined}>

      {/* Rank */}
      <div className={`w-8 flex-shrink-0 text-center font-black text-sm ${top ? s.text : 'text-[#8a94a6]'}`}>
        {top ? s.medal : rank}
      </div>

      {/* Avatar */}
      <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-base flex-shrink-0 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${col}cc, ${col}44)`, color: '#080d14' }}>
        {entry.avatar_url
          ? <img src={entry.avatar_url} alt="" className="w-full h-full object-cover" />
          : (entry.display_name || '?')[0]?.toUpperCase()}
      </div>

      {/* Name + level */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-white text-sm">{entry.display_name}</span>
          <span className="text-[11px] text-[#8a94a6]">@{entry.handle}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold border"
            style={{ color: col, borderColor: `${col}35`, background: `${col}12` }}>
            <Star className="w-2 h-2 inline mr-0.5" />Lv {lv} · {lvLabel(lv)}
          </span>
        </div>
        {(prog.badges || []).length > 0 && (
          <div className="flex gap-1 mt-1 flex-wrap">
            {prog.badges.slice(0,4).map(b => (
              <span key={b.id} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.05] text-[#8a94a6]">{b.id}</span>
            ))}
          </div>
        )}
      </div>

      {/* XP */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Zap className="w-3.5 h-3.5" style={{ color: col }} />
        <span className="font-black text-sm" style={{ color: col }}>{(prog.xp||0).toLocaleString()}</span>
        <span className="text-[10px] text-[#8a94a6]">XP</span>
      </div>

      <ChevronRight className="w-4 h-4 text-[#8a94a6] group-hover:text-[#62FAE3] flex-shrink-0 transition-colors" />
    </motion.div>
  );
}

export const NetworkLeaderboardPage = () => {
  const navigate = useNavigate();
  const [window30, setWindow30] = useState(false);
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/network/leaderboard${window30 ? '?window=30d' : ''}`)
      .then(r => setEntries(r.data.entries || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [window30]);

  /* stats */
  const totalXP = entries.reduce((s,e) => s + (e.progress?.xp||0), 0);
  const avgXP   = entries.length ? Math.round(totalXP / entries.length) : 0;
  const top     = entries[0];

  return (
    <div className="min-h-screen bg-transparent text-white relative overflow-x-hidden">
      {/* Ambient */}
      <div className="fixed top-0 left-0 w-[800px] pointer-events-none z-0 opacity-40"
        style={{ transform:'translate(-25%,-25%)' }} dangerouslySetInnerHTML={{ __html: leftTopArrowGradient }} />
      <div className="fixed bottom-0 right-0 w-[700px] pointer-events-none z-0 opacity-40"
        style={{ transform:'translate(25%,25%)' }} dangerouslySetInnerHTML={{ __html: rightBottomArrowGradient }} />

      {/* ── Hero ── */}
      <div className="relative w-full px-5 sm:px-8 pt-6 pb-5 border-b border-white/[0.06]"
        style={{ background:'linear-gradient(180deg,rgba(245,196,66,0.04) 0%,transparent 100%)' }}>
        <div className="flex items-start justify-between flex-wrap gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ background:'rgba(245,196,66,0.12)', border:'1px solid rgba(245,196,66,0.25)' }}>
                <Trophy className="w-4 h-4 text-amber-400" />
              </div>
              <span className="text-[11px] font-bold tracking-widest text-amber-400 uppercase">Leaderboard</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-none">
              <span className="text-white">Top </span>
              <span style={{ background:'linear-gradient(106deg,#f5c842 0%,#62FAE3 100%)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                Creators
              </span>
            </h1>
            <p className="text-[#8a94a6] text-sm mt-1.5">Ranked by XP earned from real studio work only</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => navigate('/network')}
              className="border-white/10 bg-white/[0.04] text-[#c5cbd6] hover:text-white hover:bg-white/[0.08]">
              <Network className="w-4 h-4 mr-1.5" /> Directory
            </Button>
            <Button size="sm" onClick={() => navigate('/network/me')}
              className="font-bold text-[#080d14]"
              style={{ background:'linear-gradient(106.58deg,#D0BCFF 0%,#62FAE3 100%)' }}>
              <Sparkles className="w-4 h-4 mr-1.5" /> My Profile
            </Button>
          </div>
        </div>

        {/* Window tabs */}
        <div className="mt-4 flex items-center gap-3 flex-wrap relative z-10">
          <div className="flex gap-1 p-1 rounded-xl bg-white/[0.04] border border-white/[0.07]">
            {[{label:'All Time',val:false},{label:'Last 30 Days',val:true}].map(opt => (
              <button key={String(opt.val)} type="button" onClick={() => setWindow30(opt.val)}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${window30===opt.val ? 'text-[#080d14]' : 'text-[#8a94a6] hover:text-white'}`}
                style={window30===opt.val ? { background:'linear-gradient(106.58deg,#f5c842 0%,#62FAE3 100%)' } : undefined}>
                {opt.label}
              </button>
            ))}
          </div>
          {!loading && <span className="text-[11px] text-[#8a94a6]">{entries.length} ranked creators</span>}
        </div>
      </div>

      {/* ── Body: table + sidebar ── */}
      <div className="flex gap-0 relative z-10">
        {/* Ranking list */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="flex justify-center py-24">
              <Loader2 className="w-8 h-8 animate-spin text-[#62FAE3]" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-24">
              <Medal className="w-14 h-14 mx-auto mb-4 text-[#62FAE3]/20" />
              <p className="text-white font-bold text-xl">No ranked creators yet</p>
              <p className="text-[#8a94a6] text-sm mt-1">Publish your profile and start creating to earn XP!</p>
              <Button className="mt-6 font-bold text-[#080d14]" onClick={() => navigate('/network/me')}
                style={{ background:'linear-gradient(106.58deg,#D0BCFF 0%,#62FAE3 100%)' }}>
                Join the network
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.05]">
              {entries.map((e, i) => (
                <RankRow key={e.user_id || i} rank={i+1} entry={e}
                  onClick={() => e.handle && navigate(`/network/${e.handle}`)} />
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="hidden lg:flex flex-col gap-4 w-72 xl:w-80 flex-shrink-0 p-5 border-l border-white/[0.06]">
          {/* Podium top 3 */}
          {!loading && entries.length >= 1 && (
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/4 p-4 space-y-3">
              <p className="text-sm font-bold text-amber-400 flex items-center gap-2">
                <Trophy className="w-4 h-4" /> Podium
              </p>
              {entries.slice(0,3).map((e,i) => {
                const s = RANK_STYLE[i];
                return (
                  <div key={e.user_id||i} onClick={() => e.handle && navigate(`/network/${e.handle}`)}
                    className="cursor-pointer flex items-center gap-2.5 p-2 rounded-xl hover:bg-white/[0.05] transition-all">
                    <span className="text-base">{s.medal}</span>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center font-black text-xs flex-shrink-0"
                      style={{ background:`linear-gradient(135deg, ${s.xpCol}cc, ${s.xpCol}44)`, color:'#080d14' }}>
                      {(e.display_name||'?')[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-white truncate">{e.display_name}</p>
                      <p className="text-[10px]" style={{color:s.xpCol}}>{(e.progress?.xp||0).toLocaleString()} XP</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Stats */}
          {!loading && entries.length > 0 && (
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-3">
              <p className="text-sm font-bold text-white flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#62FAE3]" /> Network Stats
              </p>
              {[
                { label:'Ranked Creators', val: entries.length },
                { label:'Total XP Pool', val: totalXP.toLocaleString() },
                { label:'Average XP', val: avgXP.toLocaleString() },
                { label:'Studio Leads', val: entries.filter(e=>e.progress?.level===6).length },
                { label:'Influencers (Lv5+)', val: entries.filter(e=>(e.progress?.level||1)>=5).length },
              ].map(({label,val}) => (
                <div key={label} className="flex items-center justify-between text-xs">
                  <span className="text-[#8a94a6]">{label}</span>
                  <span className="font-bold text-white">{val}</span>
                </div>
              ))}
            </div>
          )}

          {/* How XP works */}
          <div className="rounded-2xl border border-[#62FAE3]/15 bg-[#62FAE3]/4 p-4 space-y-2">
            <p className="text-sm font-bold text-[#62FAE3] flex items-center gap-2">
              <Zap className="w-4 h-4" /> How XP Works
            </p>
            {[
              ['Publish profile','50 XP'],
              ['Pin a project','20 XP'],
              ['Story / narration','25 XP'],
              ['Completed job','10 XP'],
            ].map(([label,xp]) => (
              <div key={label} className="flex items-center justify-between text-xs">
                <span className="text-[#8a94a6]">{label}</span>
                <span className="font-bold text-[#62FAE3]">+{xp}</span>
              </div>
            ))}
            <p className="text-[10px] text-[#8a94a6] pt-1 border-t border-white/[0.07]">
              DMs never award XP — only real creative work counts.
            </p>
          </div>

          {/* Level chart */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 space-y-2">
            <p className="text-sm font-bold text-white flex items-center gap-2">
              <Star className="w-4 h-4 text-[#D0BCFF]" /> Level Tiers
            </p>
            {[
              ['Newcomer','0','#94a3b8'],['Maker','100','#7dd3fc'],['Creator','300','#c4b5fd'],
              ['Featured','700','#67e8f9'],['Influencer','1,500','#f0abfc'],['Studio Lead','3,000','#62FAE3'],
            ].map(([name,min,col]) => (
              <div key={name} className="flex items-center justify-between text-xs">
                <span className="font-semibold" style={{color:col}}>{name}</span>
                <span className="text-[#8a94a6]">{min}+ XP</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkLeaderboardPage;
